import { buildCustomTrackLayout, CUSTOM_TRACK_METRES_PER_UNIT, type CustomTrackLayout } from "./customTrackLayout";
import { CUSTOM_TRACK_ID_PATTERN, CUSTOM_TRACK_PREVIEW_KEY, normalizeCustomTrackId, type CustomTrackDefinition } from "./customTrackSchema";
import { getCustomTrackTheme } from "./customTrackTheme";
import { LEGACY_TRACK_CATALOG, getLegacyTrackDefinition } from "../legacyTrackCatalog";
import { buildClassicRuntimeLayout } from "../legacyTrackLayout";
import type { ClassicProgressRules } from "./classicProgress";

export interface Vec3 { x: number; y: number; z: number; }
export interface TrackFrame { position: Vec3; tangent: Vec3; normal: Vec3; distance: number; }
export interface TrackProjection extends TrackFrame {
  lateral: number;
  distanceSq: number;
  halfWidth: number;
  surface: "main" | "shortcut";
  /** Nearest prepared centreline sample for source-indexed classic rules. */
  sampleIndex?: number;
}
export interface ShortcutSample extends TrackFrame { routeDistance: number; progress: number; }
/**
 * An authored point in a shortcut's local edge-to-edge frame.
 *
 * `position` is deliberately a Vec3 so a shortcut is real 3D route data, not
 * a renderer-only decoration: x is along the entry-to-exit chord, y is a lift
 * from the chord's interpolated height, and z is lateral to that chord. The
 * named fields are retained beside the vector to make authored layouts easy
 * to audit in data-oriented tooling.
 */
export interface ShortcutAnchor extends Vec3 {
  progress: number;
  /** Route-local coordinates: x=along, y=lift, z=lateral. */
  position: Vec3;
  along: number;
  lift: number;
  lateral: number;
}
export interface ShortcutBalanceTargets {
  /** Desired shortcut length / bypassed main-road length. */
  targetRatio: number;
  /** Executable acceptance band for authored balance. */
  minRatio: number;
  maxRatio: number;
  /** Metres saved by a clean, perfect route. Real shortcuts save 1-2 s a lap. */
  minSavings: number;
  maxSavings: number;
}
export type ShortcutStyle = "copper-pipe" | "neon-dock" | "timber-pass" | "cellar-arch" | "citrus-aqueduct" | "lunar-ring";
/**
 * What hides the entry from a casual glance. Secret shortcuts are found, not
 * signposted: the occluder is visual only and never blocks a kart.
 */
export type ShortcutOccluder = "waterfall" | "container-door" | "dust-curtain" | "cask-wall" | "hedge" | "holo-seal";
/**
 * A jump inside a shortcut. The ramp launches the kart; the gap immediately
 * after it has no floor. Launch height scales with speed, so a gap sized near
 * a full-speed flight distance is the skill gate: brake into the shortcut and
 * you fall and get reset to the last solid ground.
 */
export interface ShortcutJump {
  /** Shortcut route progress (0-1) of the ramp centre. */
  rampProgress: number;
  /** Shortcut route progress (0-1) where the floor stops and resumes. */
  gapStartProgress: number;
  gapEndProgress: number;
}
export interface ShortcutSpec {
  id: string;
  label: string;
  style: ShortcutStyle;
  /** Secret-entry dressing; see ShortcutOccluder. */
  occluder: ShortcutOccluder;
  /** Skill gates along the route; may be empty for a pure hidden cut. */
  jumps: readonly ShortcutJump[];
  startProgress: number;
  endProgress: number;
  halfWidth: number;
  anchors: readonly ShortcutAnchor[];
  /** Metres of tangent handle at the entry and exit of the smooth base arc. */
  endpointHandles: readonly [number, number];
  /** Signed road-normal side for the entry and exit portals. */
  endpointSides?: readonly [number, number];
  /** Optional authored centreline offset for a separated side branch. */
  branchOffset?: number;
  /** Bounded contribution of scenic authored offsets to the driveable base arc. */
  anchorInfluence: number;
  balance: ShortcutBalanceTargets;
}
export interface ShortcutGap { startDistance: number; endDistance: number; startRouteDistance: number; endRouteDistance: number; }
export interface ShortcutDefinition extends ShortcutSpec {
  startDistance: number;
  endDistance: number;
  start: Vec3;
  end: Vec3;
  normal: Vec3;
  length: number;
  samples: readonly ShortcutSample[];
  /** Ramps that live on the shortcut surface, in course-distance space. */
  ramps: readonly RampDefinition[];
  /** Floorless spans, in course-distance space (kart.distance while on the shortcut). */
  gaps: readonly ShortcutGap[];
}
export type RampSurface = "main" | "shortcut";
/**
 * Source-scaled jump rules carried by a source-indexed ramp.  Static v1
 * classics and editor/saved custom tracks with legacy `jumps` markers share
 * this runtime; only the classic tracks additionally opt into checkpoint
 * progress rules.
 *
 * The v1 simulation works in source pixels and triggers from the nearest
 * prepared sample index.  Keep the source index/count beside the physical
 * ramp distance so a source-indexed course cannot accidentally inherit v2's
 * distance-radius trigger or metre-native launch constants.
 */
export interface ClassicJumpRuntime {
  /** Exact `Math.round(u * N) % N` source marker. */
  sampleIndex: number;
  /** Number of source samples in the closed lap (the closing duplicate excluded). */
  sampleCount: number;
  /** v1's inclusive forward trigger window is `jp < 26`. */
  triggerWindowSamples: number;
  /** v1's re-arm condition is `jp > min(60, floor(N / 3))`. */
  rearmWindowSamples: number;
  /** Source constants converted through 0.125 world metres/source unit. */
  sourceUnitsPerMetre: number;
  launchSpeedThreshold: number;
  launchVelocityBase: number;
  launchVelocityPerSpeed: number;
  gravity: number;
  landingSpeedScale: number;
  /** v1 does not gate a jump by ramp width; road containment remains v2-owned. */
  lateralMode: "source-sample";
}
export interface RampDefinition { id: string; distance: number; halfLength: number; halfWidth: number; surface: RampSurface; classic?: ClassicJumpRuntime; }
export type SignatureZoneEffect = "boost" | "slick" | "rough" | "launch";
export interface SignatureZoneProfile {
  id: string;
  label: string;
  progress: number;
  halfLength: number;
  halfWidth: number;
  effect: SignatureZoneEffect;
  color: number;
  boostSeconds: number;
  gripScale: number;
  speedScale: number;
  steeringScale: number;
  launchVelocity: number;
}
export interface SignatureZoneDefinition extends SignatureZoneProfile { distance: number; }
export interface TrackTheme { sky: number; fog: number; ground: number; road: number; kerb: number; shortcut: number; barrierA: number; barrierB: number; accent: number; foliage: number; trunk: number; /** Optional authored road-marking tint for custom courses. */ line?: number; }
export type RouteFeatureKind = "bridge" | "tunnel" | "underground" | "crossover";
export interface RouteFeature { id: string; kind: RouteFeatureKind; startProgress: number; endProgress: number; label: string; }
export interface TrackDefinition { id: string; name: string; location: string; controlPoints: readonly Vec3[]; routeFeatures: readonly RouteFeature[]; rampProgress: readonly number[]; /** Main-road half-width in metres; built-ins use the default when omitted. */ halfWidth?: number; shortcut: ShortcutSpec | null; signatureZone: SignatureZoneProfile | null; theme: TrackTheme; }
interface TrackRuntime { definition: TrackDefinition; samples: Array<Vec3 & { distance: number }>; length: number; ramps: readonly RampDefinition[]; shortcut: ShortcutDefinition | null; }

export const TRACK_WIDTH = 20;
export const DEFAULT_TRACK_ID = "malt-mountain";
/** v1 source pixels become world metres at this fixed scale. */
export const CLASSIC_SOURCE_METRES_PER_UNIT = CUSTOM_TRACK_METRES_PER_UNIT;
export const CLASSIC_SOURCE_UNITS_PER_METRE = 1 / CLASSIC_SOURCE_METRES_PER_UNIT;
export const CLASSIC_JUMP_TRIGGER_WINDOW_SAMPLES = 26;
export const CLASSIC_JUMP_REARM_WINDOW_MAX_SAMPLES = 60;
export const CLASSIC_JUMP_SOURCE_SPEED_THRESHOLD = 150;
export const CLASSIC_JUMP_SOURCE_LAUNCH_VELOCITY = 120;
export const CLASSIC_JUMP_SOURCE_GRAVITY = 700;
export const CLASSIC_JUMP_LAUNCH_VELOCITY_PER_SPEED = 0.45;
export const CLASSIC_JUMP_LANDING_SPEED_SCALE = 0.96;
/**
 * Generic metre-based fallback envelope. Source-indexed jumps use their
 * sample profile and full-road lateral guard instead; their flat renderer
 * uses the source layout's 0.72-width, twenty-sample decal footprint.
 */
export const CUSTOM_RAMP_HALF_WIDTH_RATIO = 0.65;
const SAMPLE_COUNT = 640;
const SHORTCUT_SAMPLE_COUNT = 96;
// Keep the authored layouts at a proper kart-racing scale. The previous 0.68
// compression made the 20 m road wider than several corner radii and reduced a
// full-speed heavy kart's turning circle to an impossible line. The authored
// 1:1 scale preserves the course complexity while opening the bends, keeping
// the edge ribbons separated, and giving every lap proper race length.
const PLANAR_COURSE_SCALE = 1;

function definitionHalfWidth(definition: TrackDefinition): number {
  const halfWidth = definition.halfWidth;
  return typeof halfWidth === "number" && Number.isFinite(halfWidth) && halfWidth > 0 ? halfWidth : TRACK_WIDTH * 0.5;
}

function shortcutAnchor(progress: number, lateral: number, lift = 0, along = 0): ShortcutAnchor {
  return {
    progress,
    position: { x: along, y: lift, z: lateral },
    x: along,
    y: lift,
    z: lateral,
    along,
    lift,
    lateral,
  };
}

export const trackCatalog: readonly TrackDefinition[] = [
  {
    id: DEFAULT_TRACK_ID, name: "Malt Mountain", location: "Alpine Brewery",
    controlPoints: [
      // The opening is a proper alpine climb and descending S rather than a
      // flat run into the first generic corner.  All gameplay systems sample
      // this same centreline, so AI, ramps, pickups and Twitch racers inherit
      // the hero silhouette without a renderer-only fake.
      { x: -112, y: 0, z: 8 }, { x: -148, y: 4, z: -48 },
      // A broad alpine S gives the 20 m road, kerbs and guardrails enough
      // physical radius.  The former zig-zag doubled back inside one road
      // width twice, so its ribbons necessarily overlapped.
      { x: -130, y: 8, z: -105 }, { x: -80, y: 12, z: -145 }, { x: -20, y: 21, z: -125 },
      { x: 48, y: 23, z: -145 }, { x: 112, y: 14, z: -96 }, { x: 145, y: 7, z: -28 }, { x: 86, y: 4, z: 22 },
      { x: 18, y: 7, z: 4 }, { x: -62, y: 9, z: 62 }, { x: -114.4, y: 8.5, z: 122.8 }, { x: -42, y: 12, z: 158 },
      { x: 54, y: 16, z: 138 }, { x: 110.8, y: 11.2, z: 82.8 }, { x: 46, y: 16, z: 34 }, { x: 0, y: 18, z: 8 },
      // Keep the final flyover decisively above the opening descent.  The old
      // 11 m height left only 1-4 m between the two road ribbons, creating an
      // undeclared collision whose kerbs and sidewalls hung across the player's
      // view.  A high crest preserves the crossover and gives the chase camera
      // a clear tunnel of space before the final descent reaches the grid.
      // Carry the return arc beyond the grid before it closes. Keeping this
      // handle south of the line concentrated the full heading change at lap
      // zero, producing a 27 m hairpin and visibly bunching both rail runs.
      { x: -82, y: 15, z: 30 },
    ],
    routeFeatures: [{ id: "malt-gorge-bridge", kind: "bridge", startProgress: 0.36, endProgress: 0.48, label: "GORGE BRIDGE" }, { id: "malt-brewery-overpass", kind: "bridge", startProgress: 0.86, endProgress: 0.93, label: "BREWERY OVERPASS" }, { id: "malt-return-viaduct", kind: "bridge", startProgress: 0.93, endProgress: 0.988, label: "RETURN VIADUCT" }, { id: "malt-brewery-crossing-low", kind: "crossover", startProgress: 0.45, endProgress: 0.51, label: "BREWERY CROSSING" }, { id: "malt-brewery-crossing-high", kind: "crossover", startProgress: 0.86, endProgress: 0.93, label: "BREWERY CROSSING" }],
    rampProgress: [0.17, 0.68],
    shortcut: {
      id: "malt-falls-gorge-run", label: "MALT FALLS GORGE RUN", style: "copper-pipe", occluder: "waterfall",
      // Secret: the alpine waterfall beside the run-in to the gorge bridge hides
      // a break in the rail. Drop through the curtain into the river bed, run
      // under the bridge, jump the broken weir and climb back out ahead of it.
      startProgress: 0.328, endProgress: 0.477, halfWidth: 3.8,
      anchors: [
        shortcutAnchor(0, 0), shortcutAnchor(0.2, 1.5, -9), shortcutAnchor(0.5, -1, -12.5),
        shortcutAnchor(0.8, 1, -8.5), shortcutAnchor(1, 0),
      ],
      jumps: [{ rampProgress: 0.585, gapStartProgress: 0.605, gapEndProgress: 0.795 }],
      endpointHandles: [28, 30], anchorInfluence: 1,
      balance: { targetRatio: 0.69, minRatio: 0.55, maxRatio: 0.8, minSavings: 55, maxSavings: 105 },
    },
    signatureZone: { id: "malt-steam-draft", label: "BREWERY STEAM DRAFT", progress: 0.405, halfLength: 8, halfWidth: 8.5, effect: "boost", color: 0xf4d76d, boostSeconds: 0.92, gripScale: 1, speedScale: 1.06, steeringScale: 1, launchVelocity: 0 },
    theme: { sky: 0x9cc7d0, fog: 0xa8ccd0, ground: 0x1d5a36, road: 0xffffff, kerb: 0xe7d9b5, shortcut: 0xd8b14a, barrierA: 0xd84b35, barrierB: 0xf0e6ce, accent: 0xf3c552, foliage: 0x225d35, trunk: 0x5d3c24 },
  },
  {
    id: "neon-harbor", name: "Neon Harbor", location: "Midnight Docks",
    controlPoints: [
      // A dock flyover climbs between stacked yards and drops back toward the
      // water. This is authoritative geometry, not a visual bridge laid over
      // a flat simulation path.
      { x: -126, y: 0, z: 4 }, { x: -154, y: 1, z: -62 }, { x: -104, y: 3, z: -132 }, { x: -22, y: 9, z: -156 },
      { x: 62, y: 12, z: -128 }, { x: 132, y: 8, z: -72 }, { x: 146, y: 2, z: -4 }, { x: 74, y: 1, z: 42 },
      { x: 4, y: 0, z: 4 },
      // Break away from Moon Tap One's near-identical inner return. Neon now
      // threads a broad loading-yard dogleg: west along the container rows,
      // around the dock head, then east toward the illuminated flyover. The
      // generous handles keep it playable at speed instead of replacing the
      // old duplicate curve with square ninety-degree corners.
      { x: -38, y: 1, z: 88 }, { x: -130, y: 2, z: 78 }, { x: -160, y: 4, z: 140 },
      { x: -86, y: 7, z: 182 }, { x: 30, y: 9, z: 174 }, { x: 126, y: 11, z: 110 },
      // Lift the whole flyover approach, not just its crossing centre. With a
      // 20 m road the former 4-7 m approach gap let the low ribbon visibly
      // occupy the upper driving lane well before the declared crossover.
      { x: 115, y: 13, z: 54 }, { x: 42, y: 15, z: 28 }, { x: -10, y: 16, z: 0 }, { x: -77, y: 7, z: -8.4 },
    ],
    routeFeatures: [{ id: "neon-container-flyover", kind: "bridge", startProgress: 0.2, endProgress: 0.34, label: "CONTAINER FLYOVER" }, { id: "neon-crossroads-flyover", kind: "bridge", startProgress: 0.85, endProgress: 0.92, label: "NEON FLYOVER" }, { id: "neon-underpass", kind: "tunnel", startProgress: 0.4, endProgress: 0.47, label: "NEON UNDERPASS" }, { id: "neon-crossroads-low", kind: "crossover", startProgress: 0.405, endProgress: 0.445, label: "DOCK CROSSROADS" }, { id: "neon-crossroads-high", kind: "crossover", startProgress: 0.88, endProgress: 0.92, label: "DOCK CROSSROADS" }],
    rampProgress: [0.24, 0.73],
    shortcut: {
      id: "neon-container-alley", label: "CONTAINER ALLEY", style: "neon-dock", occluder: "container-door",
      // Secret: one container in the stack behind the upper bend stands with its
      // doors ajar. The alley behind it runs across the yard and a loading ramp
      // throws you over the slipway onto the high pier road.
      startProgress: 0.71, endProgress: 0.86, halfWidth: 3.6,
      anchors: [
        shortcutAnchor(0, 0), shortcutAnchor(0.3, 2, -2.5), shortcutAnchor(0.55, -1.5, -2),
        shortcutAnchor(0.8, 0.5, 0.5), shortcutAnchor(1, 0),
      ],
      jumps: [{ rampProgress: 0.6, gapStartProgress: 0.62, gapEndProgress: 0.84 }],
      endpointHandles: [28, 30], anchorInfluence: 1,
      balance: { targetRatio: 0.62, minRatio: 0.55, maxRatio: 0.8, minSavings: 55, maxSavings: 105 },
    },
    signatureZone: { id: "neon-dock-slide", label: "NEON DOCK SLIDE", progress: 0.49, halfLength: 8.5, halfWidth: 8.6, effect: "slick", color: 0x4ef4ff, boostSeconds: 0, gripScale: 0.24, speedScale: 1, steeringScale: 0.86, launchVelocity: 0 },
    theme: { sky: 0x071426, fog: 0x10283b, ground: 0x102d34, road: 0x9fb2bb, kerb: 0x173f4c, shortcut: 0x18b7b2, barrierA: 0xff4f93, barrierB: 0x43e4ff, accent: 0x55f6de, foliage: 0x16545b, trunk: 0x233a4a },
  },
  {
    id: "dustbowl-draft", name: "Dustbowl Draft", location: "Sunset Roadhouse",
    controlPoints: [
      // The opening climbs onto a mesa rim, commits to an exposed timber-
      // trestle crest, then drops hard back into the basin. This silhouette is
      // authoritative: driving, AI, Twitch autoplay and rendering all traverse
      // the same rise and descent rather than a decorative bridge over flat road.
      { x: -165, y: 0, z: -105 }, { x: -130, y: 4, z: -150 }, { x: -20, y: 8, z: -164 }, { x: 70, y: 13, z: -150 },
      { x: 142, y: 10, z: -100 }, { x: 158, y: 3, z: -28 }, { x: 120, y: 1, z: 28 }, { x: 55, y: 1, z: 42 },
      { x: -20, y: 0, z: 30 }, { x: -85, y: 2, z: 52 }, { x: -138, y: 4, z: 105 }, { x: -80, y: 7, z: 150 },
      { x: 10, y: 10, z: 158 }, { x: 88, y: 11, z: 132 }, { x: 145, y: 12, z: 82 }, { x: 110, y: 11, z: 30 },
      // Close the lap with one continuous descending arc. The former final
      // point at z=-28 made the Catmull-Rom spline reverse toward the basin
      // before snapping back to the grid, folding its kerb across the lane.
      // The final descent now changes direction twice before the grid.  The
      // broad radii preserve Heavy-kart drivability while breaking the old
      // one-note oval return into a readable desert S-complex.
      { x: 45, y: 9, z: -3 }, { x: 2, y: 7, z: -57 }, { x: -57, y: 4, z: -27 }, { x: -130, y: 2, z: -72 },
    ],
    routeFeatures: [{ id: "dust-mesa-trestle", kind: "bridge", startProgress: 0.1, endProgress: 0.23, label: "MESA TRESTLE" }, { id: "dust-wash-trestle", kind: "bridge", startProgress: 0.72, endProgress: 0.79, label: "WASH TRESTLE" }, { id: "dust-canyon-pass", kind: "tunnel", startProgress: 0.43, endProgress: 0.51, label: "CANYON PASS" }, { id: "dust-wash-crossing-low", kind: "crossover", startProgress: 0.28, endProgress: 0.34, label: "DRY WASH CROSSING" }, { id: "dust-wash-crossing-high", kind: "crossover", startProgress: 0.72, endProgress: 0.79, label: "DRY WASH CROSSING" }],
    rampProgress: [0.14, 0.55],
    shortcut: {
      id: "dust-rattler-slot", label: "RATTLER SLOT CANYON", style: "timber-pass", occluder: "dust-curtain",
      // Secret: a crack in the canyon wall before the pass, veiled by blowing
      // dust. A slot canyon threads the mesa; halfway through, the old plank
      // bridge has lost a span and you have to fly it.
      startProgress: 0.395, endProgress: 0.565, halfWidth: 3.5,
      anchors: [
        shortcutAnchor(0, 0), shortcutAnchor(0.25, -1.5, -1), shortcutAnchor(0.5, 1.5, 0.5),
        shortcutAnchor(0.78, -1, 1), shortcutAnchor(1, 0),
      ],
      jumps: [{ rampProgress: 0.5, gapStartProgress: 0.52, gapEndProgress: 0.69 }],
      endpointHandles: [28, 90], anchorInfluence: 1,
      balance: { targetRatio: 0.69, minRatio: 0.55, maxRatio: 0.8, minSavings: 55, maxSavings: 105 },
    },
    signatureZone: { id: "dustbowl-trestle-rumble", label: "TRESTLE RUMBLE", progress: 0.345, halfLength: 9, halfWidth: 8.7, effect: "rough", color: 0xffb44f, boostSeconds: 0, gripScale: 0.72, speedScale: 0.78, steeringScale: 0.92, launchVelocity: 0 },
    theme: { sky: 0xe77f4d, fog: 0xe39b63, ground: 0x8d4d2d, road: 0xe6c59a, kerb: 0x6f3825, shortcut: 0xf0a02d, barrierA: 0x3b7f79, barrierB: 0xf5dfb5, accent: 0xffd14b, foliage: 0x537446, trunk: 0x5b3525 },
  },
  {
    id: "keg-catacombs", name: "Keg Catacombs", location: "Old Cellar Vaults",
    controlPoints: [
      // The grid begins in the upper cellar and commits immediately to a long
      // vaulted plunge before the lower crypt winds back uphill. This is the
      // authoritative course surface used by human physics, AI and Twitch.
      { x: -112, y: 10, z: 0 }, { x: -155, y: 8, z: -32 }, { x: -150, y: 3, z: -110 }, { x: -82, y: -4, z: -156 },
      { x: 20, y: -8, z: -150 }, { x: 102, y: -7, z: -118 }, { x: 150, y: -3, z: -50 }, { x: 145, y: -1, z: 20 },
      { x: 80, y: -1, z: 38 }, { x: 8, y: -2, z: 4 }, { x: -60, y: 0, z: 70 }, { x: -129, y: 4.9, z: 111 },
      { x: -110, y: 9, z: 160 }, { x: -20, y: 11, z: 170 }, { x: 80, y: 10, z: 150 }, { x: 145, y: 8, z: 90 },
      { x: 125, y: 8, z: 40 }, { x: 48, y: 9, z: 32 }, { x: -2, y: 8, z: 6 }, { x: -68, y: 10, z: -44 },
    ],
    routeFeatures: [{ id: "cat-cask-tunnel", kind: "tunnel", startProgress: 0.1, endProgress: 0.36, label: "CASK TUNNEL" }, { id: "cat-lower-vaults", kind: "underground", startProgress: 0.36, endProgress: 0.44, label: "LOWER VAULTS" }, { id: "cat-cellar-bridge", kind: "bridge", startProgress: 0.83, endProgress: 0.89, label: "CELLAR BRIDGE" }, { id: "cat-cellar-crossing-low", kind: "crossover", startProgress: 0.38, endProgress: 0.44, label: "CELLAR CROSSING" }, { id: "cat-cellar-crossing-high", kind: "crossover", startProgress: 0.83, endProgress: 0.89, label: "CELLAR CROSSING" }],
    rampProgress: [0.26, 0.65],
    shortcut: {
      id: "keg-cask-wall-breach", label: "CASK WALL BREACH", style: "cellar-arch", occluder: "cask-wall",
      // Secret: a wall of stacked casks on the outer bend is a false front. The
      // cellar passage behind it cuts the whole upper loop; the floor has
      // collapsed into the deep vault partway along.
      startProgress: 0.74, endProgress: 0.89, halfWidth: 3.6,
      anchors: [
        shortcutAnchor(0, 0), shortcutAnchor(0.3, -2.5, -1.5), shortcutAnchor(0.55, 2, -1),
        shortcutAnchor(0.8, -1, 0), shortcutAnchor(1, 0),
      ],
      jumps: [{ rampProgress: 0.52, gapStartProgress: 0.54, gapEndProgress: 0.76 }],
      endpointHandles: [28, 30], anchorInfluence: 1,
      balance: { targetRatio: 0.59, minRatio: 0.55, maxRatio: 0.8, minSavings: 55, maxSavings: 105 },
    },
    signatureZone: { id: "catacomb-cellar-slick", label: "CELLAR SLICK", progress: 0.44, halfLength: 8, halfWidth: 8.4, effect: "slick", color: 0xf19b55, boostSeconds: 0, gripScale: 0.34, speedScale: 0.94, steeringScale: 1.08, launchVelocity: 0 },
    theme: { sky: 0x160d1d, fog: 0x2b1830, ground: 0x281c28, road: 0x96878f, kerb: 0x3c263c, shortcut: 0x9b4f78, barrierA: 0x6e3e59, barrierB: 0xc7b3a0, accent: 0xf0a35c, foliage: 0x40334b, trunk: 0x4d3028 },
  },
  {
    id: "citrus-courtyard", name: "Citrus Courtyard", location: "Coastal Tapas Quarter",
    controlPoints: [
      // Citrus is a coastal serpentine rather than another wide figure-eight.
      // The opening is a low quay run from the harbour, wrapping a broad
      // right-hand hairpin onto the upper terrace before returning west.
      // Everything below is authoritative route data shared by human physics,
      // AI, Twitch racers, ramps, scenery and the bridge sampler.
      { x: -110, y: 2, z: 24 }, { x: -70, y: 2, z: 26 }, { x: -15, y: 2, z: 28 }, { x: 45, y: 2, z: 28 },
      { x: 95, y: 2, z: 24 }, { x: 130, y: 3, z: 14 }, { x: 150, y: 4, z: -4 }, { x: 158, y: 3, z: -40 },
      { x: 150, y: 5, z: -75 }, { x: 121, y: 10, z: -105 }, { x: 75, y: 12, z: -126 }, { x: 18, y: 13, z: -138 },
      { x: -42, y: 13, z: -136 }, { x: -93, y: 11, z: -122 }, { x: -128, y: 9, z: -98 }, { x: -148, y: 8, z: -68 },
      // The left terrace hairpin turns the route back into the market quarter.
      // Alternating 20-25 m offsets make four readable slalom beats while the
      // fairing pass keeps every bend within the Heavy-kart radius contract.
      { x: -150, y: 7, z: -38 }, { x: -133, y: 3, z: -14 }, { x: -102, y: -1, z: -2 }, { x: -70, y: 4.2, z: -28 },
      { x: -37, y: 4.5, z: -4 }, { x: -4, y: 10, z: -28 }, { x: 29, y: 15, z: -4 }, { x: 62, y: 8, z: -26 },
      { x: 94, y: -1, z: -2 }, { x: 122, y: 0, z: -35 },
      // A lifted return road crosses the opening quay as a long festival
      // aqueduct. Its high deck stays more than a road-width above the lower
      // approach, then descends only at the final shore-side closure.
      { x: 150, y: 14, z: -60 }, { x: 175, y: 17, z: -45 }, { x: 185, y: 18, z: -10 }, { x: 178, y: 19, z: 30 },
      // The upper terrace bends north into a broad scenic S before the shore
      // return. It gives the fixed Citrus shortcut fractions a real corner to
      // cut and makes this route read unlike the other broad loop courses.
      { x: 160, y: 18, z: 72 }, { x: 155, y: 20, z: 115 }, { x: 75, y: 20, z: 145 },
      // A long, raised westbound terrace keeps the return road legible from
      // the plaza. The broad U below it is deliberately laid out as a scenic
      // shore-side switchback, with enough spacing for a 20 m road ribbon and
      // no same-height pinch at the return closure.
      { x: 10, y: 20, z: 150 }, { x: -50, y: 20, z: 150 }, { x: -110, y: 20, z: 150 }, { x: -270, y: 20, z: 150 },
      { x: -315, y: 17, z: 135 }, { x: -333, y: 14, z: 95 }, { x: -315, y: 11, z: 55 }, { x: -270, y: 7, z: 24 },
      // Descend along the shore-side straight into the low quay start. Keep
      // the grade separation until the final approach so the aqueduct crossing
      // near the market hairpin remains an actual over-under section.
      { x: -252.222, y: 6.444, z: 24 }, { x: -234.444, y: 5.889, z: 24 }, { x: -216.667, y: 5.333, z: 24 },
      { x: -198.889, y: 4.778, z: 24 }, { x: -181.111, y: 4.222, z: 24 }, { x: -163.333, y: 3.667, z: 24 },
      { x: -145.556, y: 3.111, z: 24 }, { x: -127.778, y: 2.556, z: 24 }, { x: -110, y: 2, z: 24 },
    ],
    routeFeatures: [{ id: "citrus-aqueduct", kind: "bridge", startProgress: 0.53, endProgress: 0.67, label: "AQUEDUCT RUN" }, { id: "citrus-festival-bridge", kind: "bridge", startProgress: 0.8, endProgress: 0.98, label: "FESTIVAL BRIDGE" }, { id: "citrus-market-arch", kind: "tunnel", startProgress: 0.4, endProgress: 0.52, label: "MARKET ARCHES" }, { id: "citrus-plaza-crossing-low", kind: "crossover", startProgress: 0, endProgress: 0.17, label: "FESTIVAL CROSSING" }, { id: "citrus-plaza-crossing-high", kind: "crossover", startProgress: 0.5, endProgress: 0.525, label: "FESTIVAL CROSSING" }],
    rampProgress: [0.2, 0.56],
    shortcut: {
      id: "citrus-grove-terrace", label: "GROVE TERRACE JUMP", style: "citrus-aqueduct", occluder: "hedge",
      // Secret: a thin stretch of hedge on the inside of the upper bend gives
      // way. Climb the orange-grove steps and jump the courtyard fountain onto
      // the terrace road above.
      // The rejoin is deliberately on the long shore-side straight instead of
      // the preceding hairpin. This leaves the shortcut with enough room for a
      // real Heavy-kart U-turn while the small portal offset drops the branch
      // back inside the main road's usable edge on exit.
      startProgress: 0.735, endProgress: 0.975, halfWidth: 3.6, branchOffset: 5,
      anchors: [
        // A long westbound leg, a broad outside U-turn, and a straight
        // eastbound shore return. The local offsets are intentionally dense:
        // they keep the authored turn smooth after the bounded sin² envelope
        // is applied by the shared shortcut sampler.
        shortcutAnchor(0, 0),
        shortcutAnchor(0.1, 26.104902, 0, 61.782063),
        shortcutAnchor(0.2, 51.642634, 0, 17.611039),
        shortcutAnchor(0.3, 66.376339, 0, 6.130281),
        shortcutAnchor(0.4, 74.210536, 0, 15.71222),
        shortcutAnchor(0.5, 67.073648, 0, 37.553443),
        shortcutAnchor(0.6, 47.765822, 0, 54.938139),
        shortcutAnchor(0.7, 20.997175, 0, 53.349495),
        shortcutAnchor(0.8, 21.07375, 0, 49.604464),
        shortcutAnchor(0.9, 32.232907, 0, 54.04272),
        shortcutAnchor(1, 0),
      ],
      jumps: [{ rampProgress: 0.6, gapStartProgress: 0.62, gapEndProgress: 0.7 }],
      endpointHandles: [140, 70], anchorInfluence: 1,
      balance: { targetRatio: 0.58, minRatio: 0.55, maxRatio: 0.8, minSavings: 55, maxSavings: 105 },
    },
    signatureZone: { id: "citrus-aqueduct-surge", label: "AQUEDUCT SURGE", progress: 0.57, halfLength: 8.5, halfWidth: 8.7, effect: "boost", color: 0x61e5e3, boostSeconds: 0.62, gripScale: 0.72, speedScale: 1.03, steeringScale: 0.96, launchVelocity: 0 },
    theme: { sky: 0x72d6dc, fog: 0x8fcfc8, ground: 0xd99058, road: 0xf1dfbd, kerb: 0x227f83, shortcut: 0xe6aa36, barrierA: 0x168c91, barrierB: 0xfff1d2, accent: 0xffca3a, foliage: 0x397d4e, trunk: 0x7b4a2d },
  },
  {
    id: "moon-tap-one", name: "Moon Tap One", location: "Orbital Brewery",
    controlPoints: [
      // A lunar mass-driver climb commits the field to a high exposed ridge,
      // launches through the orbital accelerator, then dives into the crater
      // floor. Human physics, AI and Twitch racers all own the same silhouette.
      { x: -118, y: 0, z: 4 }, { x: -160, y: 4, z: -50 }, { x: -140, y: 12, z: -120 }, { x: -70, y: 22, z: -165 },
      { x: 20, y: 28, z: -170 }, { x: 110, y: 22, z: -140 }, { x: 170, y: 12, z: -70 }, { x: 175, y: 4, z: 10 },
      // Widen the opposing crater-floor bends without disturbing either
      // grade-separated crossing.  This gives the tunnel exit a genuine S
      // instead of repeating Citrus Courtyard's rounded inner return.
      { x: 120, y: -2, z: 55 }, { x: 12, y: -5, z: 4 }, { x: -20, y: -2, z: 60 }, { x: -105, y: 7, z: 105 },
      { x: -70, y: 15, z: 160 }, { x: 20, y: 18, z: 175 }, { x: 100, y: 16, z: 150 }, { x: 150, y: 12, z: 100 },
      { x: 110, y: 13, z: 55 }, { x: 45, y: 14, z: 30 }, { x: -2, y: 14, z: 10 }, { x: -73.2, y: 5, z: -13.4 },
    ],
    routeFeatures: [{ id: "moon-orbital-bridge", kind: "bridge", startProgress: 0.18, endProgress: 0.35, label: "ORBITAL BRIDGE" }, { id: "moon-mass-bridge", kind: "bridge", startProgress: 0.8, endProgress: 0.87, label: "MASS BRIDGE" }, { id: "moon-crater-tunnel", kind: "underground", startProgress: 0.38, endProgress: 0.46, label: "CRATER TUNNEL" }, { id: "moon-mass-crossing-low", kind: "crossover", startProgress: 0.4, endProgress: 0.46, label: "MASS CROSSING" }, { id: "moon-mass-crossing-high", kind: "crossover", startProgress: 0.8, endProgress: 0.87, label: "MASS CROSSING" }],
    rampProgress: [0.16, 0.34, 0.91],
    shortcut: {
      id: "moon-service-tube", label: "SERVICE TUBE", style: "lunar-ring", occluder: "holo-seal",
      // Secret: a hatch on the outer wall reads SEALED in hologram, but the
      // seal is light. The maintenance tube behind it cuts under the ring; a
      // hull breach halfway along has to be jumped in the dark.
      startProgress: 0.723, endProgress: 0.873, halfWidth: 3.6,
      anchors: [
        shortcutAnchor(0, 0), shortcutAnchor(0.3, -2, -1.5), shortcutAnchor(0.55, 1.5, -1),
        shortcutAnchor(0.8, -0.5, 0), shortcutAnchor(1, 0),
      ],
      jumps: [{ rampProgress: 0.5, gapStartProgress: 0.52, gapEndProgress: 0.765 }],
      endpointHandles: [28, 30], anchorInfluence: 1,
      balance: { targetRatio: 0.58, minRatio: 0.55, maxRatio: 0.8, minSavings: 55, maxSavings: 105 },
    },
    signatureZone: { id: "moon-mass-driver", label: "MASS DRIVER", progress: 0.415, halfLength: 9.5, halfWidth: 8.8, effect: "launch", color: 0x8f7bff, boostSeconds: 1.05, gripScale: 1, speedScale: 1.08, steeringScale: 1, launchVelocity: 12.5 },
    theme: { sky: 0x050713, fog: 0x15162d, ground: 0x575a68, road: 0xc4c7d2, kerb: 0x393c56, shortcut: 0x9d68ff, barrierA: 0x6c42c7, barrierB: 0xe3e6f2, accent: 0x69f4ff, foliage: 0x4b4e63, trunk: 0x343646 },
  },
];

const definitionById = new Map(trackCatalog.map((definition) => [definition.id, definition]));
const runtimeById = new Map<string, TrackRuntime>();
const customDefinitionById = new Map<string, TrackDefinition>();
const customLayoutById = new Map<string, CustomTrackLayout>();

/** Static originals are separate from both the authored tour and saved c_ tracks.
 * Listing them must not eagerly prepare thirteen complete course runtimes. */
export const classicTrackCatalog = Object.freeze(LEGACY_TRACK_CATALOG.map(track => Object.freeze({
  id: `classic-${track.id}`, name: track.name, location: "V1 Classic", legacyId: track.id,
})));

export function isClassicTrackId(id: string): boolean {
  return id.startsWith("classic-") && getLegacyTrackDefinition(id.slice(8)) !== undefined;
}

const classicProgressRules = new Map<string, ClassicProgressRules>();

/** Immutable source-index rules, separate from saved custom definitions. */
export function getClassicTrackRules(id: string): ClassicProgressRules | null {
  if (!isClassicTrackId(id)) return null;
  const cached = classicProgressRules.get(id);
  if (cached) return cached;
  const layout = getCustomTrackLayout(id)!;
  const count = layout.samples.length - 1;
  const rules = Object.freeze({
    samples: layout.samples,
    length: layout.length,
    checkpoints: Object.freeze(Array.from({ length: 8 }, (_, i) => Math.floor(i * count / 8))),
    gateHalfWidth: (layout.definition.width + 8) * CUSTOM_TRACK_METRES_PER_UNIT,
  });
  classicProgressRules.set(id, rules);
  return rules;
}

function ensureClassicTrack(id: string): void {
  if (!id.startsWith("classic-")) return;
  if (!isClassicTrackId(id)) throw new Error(`Unknown classic track "${id}"`);
  if (customDefinitionById.has(id)) return;
  const layout = freezeCustomTrackLayout(buildClassicRuntimeLayout(id.slice(8)));
  const definition = freezeTrackDefinition({ ...customTrackDefinition(id, layout), location: "V1 Classic" });
  customLayoutById.set(id, layout);
  customDefinitionById.set(id, definition);
}

function isCustomTrackId(id: string): boolean {
  return id === CUSTOM_TRACK_PREVIEW_KEY || CUSTOM_TRACK_ID_PATTERN.test(id);
}

function normalizeCustomRegistryId(id: string): string {
  if (id === CUSTOM_TRACK_PREVIEW_KEY) return id;
  return normalizeCustomTrackId(id);
}

function customTrackDefinition(id: string, layout: CustomTrackLayout): TrackDefinition {
  const { definition } = layout;
  return {
    id,
    name: definition.name,
    location: "Custom Course",
    controlPoints: definition.raw.map(([x, z]) => ({ x: x * CUSTOM_TRACK_METRES_PER_UNIT, y: 0, z: z * CUSTOM_TRACK_METRES_PER_UNIT })),
    routeFeatures: customRouteFeatures(id, layout),
    // TrackDefinition consumers treat rampProgress as physical lap fractions;
    // convert the editor's sample-index markers through the prepared layout.
    rampProgress: (definition.jumps ?? []).map((progress) => customProgressAtMarker(layout, progress)),
    halfWidth: layout.halfWidth,
    shortcut: null,
    signatureZone: null,
    theme: getCustomTrackTheme(definition),
  };
}

function freezeCustomTrackDefinition(definition: CustomTrackDefinition): CustomTrackDefinition {
  for (const point of definition.raw) Object.freeze(point);
  Object.freeze(definition.raw);
  if (definition.jumps) Object.freeze(definition.jumps);
  if (definition.tunnels) {
    for (const tunnel of definition.tunnels) Object.freeze(tunnel);
    Object.freeze(definition.tunnels);
  }
  if (definition.items) Object.freeze(definition.items);
  if (definition.dips) Object.freeze(definition.dips);
  if (definition.tint) Object.freeze(definition.tint);
  return Object.freeze(definition);
}

function freezeCustomTrackLayout(layout: CustomTrackLayout): CustomTrackLayout {
  freezeCustomTrackDefinition(layout.definition);
  for (const sample of layout.samples) Object.freeze(sample);
  Object.freeze(layout.samples);
  for (const bridge of layout.bridges) Object.freeze(bridge);
  Object.freeze(layout.bridges);
  Object.freeze(layout.itemProgress);
  return Object.freeze(layout);
}

function freezeTrackDefinition(definition: TrackDefinition): TrackDefinition {
  for (const point of definition.controlPoints) Object.freeze(point);
  Object.freeze(definition.controlPoints);
  for (const feature of definition.routeFeatures) Object.freeze(feature);
  Object.freeze(definition.routeFeatures);
  Object.freeze(definition.rampProgress);
  Object.freeze(definition.theme);
  return Object.freeze(definition);
}

function catmullRom(t: number, p0: number, p1: number, p2: number, p3: number): number {
  const t2 = t * t; const t3 = t2 * t;
  return 0.5 * ((2 * p1) + (-p0 + p2) * t + (2 * p0 - 5 * p1 + 4 * p2 - p3) * t2 + (-p0 + 3 * p1 - 3 * p2 + p3) * t3);
}

function frameFromSegment(runtime: Pick<TrackRuntime, "samples">, index: number, t: number, distance: number): TrackFrame {
  const a = runtime.samples[index]; const b = runtime.samples[index + 1];
  const dx = b.x - a.x; const dy = b.y - a.y; const dz = b.z - a.z;
  const length = Math.hypot(dx, dy, dz) || 1; const flatLength = Math.hypot(dx, dz) || 1;
  return { position: { x: a.x + dx * t, y: a.y + dy * t, z: a.z + dz * t }, tangent: { x: dx / length, y: dy / length, z: dz / length }, normal: { x: dz / flatLength, y: 0, z: -dx / flatLength }, distance };
}

function sampleRuntime(runtime: Pick<TrackRuntime, "samples" | "length">, distance: number): TrackFrame {
  const wrapped = ((distance % runtime.length) + runtime.length) % runtime.length;
  let low = 0; let high = runtime.samples.length - 2;
  while (low <= high) {
    const middle = (low + high) >> 1;
    if (runtime.samples[middle + 1].distance < wrapped) low = middle + 1;
    else if (runtime.samples[middle].distance > wrapped) high = middle - 1;
    else {
      const span = runtime.samples[middle + 1].distance - runtime.samples[middle].distance || 1;
      return frameFromSegment(runtime, middle, (wrapped - runtime.samples[middle].distance) / span, wrapped);
    }
  }
  return frameFromSegment(runtime, 0, 0, wrapped);
}

/**
 * Legacy custom features are authored as sample-index markers. The legacy
 * runtime converts them with `Math.round(u * N) % N`, rather than preserving a
 * continuous fraction. Keep that quantization at the v2 boundary so jumps,
 * tunnels and any wrapped endpoint land on exactly the same prepared sample.
 */
function customSampleIndex(layout: CustomTrackLayout, progress: number): number {
  const count = layout.samples.length - 1;
  if (count <= 0) return 0;
  return ((Math.round(progress * count) % count) + count) % count;
}

function customProgressAtIndex(layout: CustomTrackLayout, index: number): number {
  const count = layout.samples.length - 1;
  if (count <= 0 || !Number.isFinite(layout.length) || layout.length <= 0) return 0;
  const wrapped = ((Math.round(index) % count) + count) % count;
  return layout.samples[wrapped].distance / layout.length;
}

function customProgressAtMarker(layout: CustomTrackLayout, progress: number): number {
  return customProgressAtIndex(layout, customSampleIndex(layout, progress));
}

function classicJumpRuntime(layout: CustomTrackLayout, progress: number): ClassicJumpRuntime {
  const sampleCount = Math.max(1, layout.samples.length - 1);
  return {
    sampleIndex: customSampleIndex(layout, progress),
    sampleCount,
    triggerWindowSamples: CLASSIC_JUMP_TRIGGER_WINDOW_SAMPLES,
    rearmWindowSamples: Math.min(CLASSIC_JUMP_REARM_WINDOW_MAX_SAMPLES, Math.floor(sampleCount / 3)),
    sourceUnitsPerMetre: CLASSIC_SOURCE_UNITS_PER_METRE,
    launchSpeedThreshold: CLASSIC_JUMP_SOURCE_SPEED_THRESHOLD * CLASSIC_SOURCE_METRES_PER_UNIT,
    launchVelocityBase: CLASSIC_JUMP_SOURCE_LAUNCH_VELOCITY * CLASSIC_SOURCE_METRES_PER_UNIT,
    launchVelocityPerSpeed: CLASSIC_JUMP_LAUNCH_VELOCITY_PER_SPEED,
    gravity: CLASSIC_JUMP_SOURCE_GRAVITY * CLASSIC_SOURCE_METRES_PER_UNIT,
    landingSpeedScale: CLASSIC_JUMP_LANDING_SPEED_SCALE,
    lateralMode: "source-sample",
  };
}

function customRouteFeatures(id: string, layout: CustomTrackLayout): RouteFeature[] {
  const labelPrefix = isClassicTrackId(id) ? "CLASSIC" : "CUSTOM";
  const count = layout.samples.length - 1;
  if (count <= 0 || !Number.isFinite(layout.length) || layout.length <= 0) return [];
  const span = (startIndex: number, endIndex: number) => ({
    startProgress: customProgressAtIndex(layout, startIndex),
    endProgress: customProgressAtIndex(layout, endIndex),
  });
  const features: RouteFeature[] = [];
  const appendFeature = (featureId: string, kind: RouteFeatureKind, startProgress: number, endProgress: number, label: string): void => {
    const epsilon = 1e-9;
    if (Math.abs(startProgress - endProgress) <= epsilon) return;
    if (startProgress < endProgress) {
      features.push({ id: featureId, kind, startProgress, endProgress, label });
      return;
    }
    // RouteFeature consumers iterate ordinary increasing intervals. Preserve a
    // legacy span crossing lap zero as two stable pieces instead of exposing a
    // start>end interval that would disappear from terrain/HUD/trestle scans.
    if (startProgress < 1 - epsilon) features.push({ id: `${featureId}-a`, kind, startProgress, endProgress: 1, label });
    if (endProgress > epsilon) features.push({ id: `${featureId}-b`, kind, startProgress: 0, endProgress, label });
  };
  layout.bridges.forEach((bridge, index) => {
    const range = span(bridge.overIndex - bridge.half, bridge.overIndex + bridge.half);
    appendFeature(`${id}-bridge-${index + 1}`, "bridge", range.startProgress, range.endProgress, `${labelPrefix} BRIDGE ${index + 1}`);
  });
  for (const [index, tunnel] of (layout.definition.tunnels ?? []).entries()) {
    const startProgress = customProgressAtMarker(layout, tunnel.u0);
    const endProgress = customProgressAtMarker(layout, tunnel.u1);
    appendFeature(`${id}-tunnel-${index + 1}`, "tunnel", startProgress, endProgress, `${labelPrefix} TUNNEL ${index + 1}`);
  }
  return features;
}

function buildRuntimeFromCustomLayout(definition: TrackDefinition, layout: CustomTrackLayout): TrackRuntime {
  // The layout builder has already performed the legacy spline sampling and
  // arc-length/elevation pass. Keep those samples verbatim so custom courses
  // cannot be silently re-smoothed by the built-in Catmull-Rom runtime.
  const samples: TrackRuntime["samples"] = layout.samples.map((sample) => ({
    x: sample.x, y: sample.y, z: sample.z, distance: sample.distance,
  }));
  // The source marker/latch runtime is also used by saved/editor custom
  // courses that carry the legacy `jumps` array.  Keep checkpoint progress
  // separate: only static classic IDs receive `getClassicTrackRules()`.
  const sourceJumpMarkers = layout.definition.jumps ?? [];
  const ramps: RampDefinition[] = definition.rampProgress.map((progress, index) => {
    const sourceProgress = sourceJumpMarkers[index];
    const classicJump = sourceProgress === undefined ? undefined : classicJumpRuntime(layout, sourceProgress);
    const ramp: RampDefinition = {
      id: `${definition.id}-ramp-${index + 1}`,
      distance: progress * layout.length,
      halfLength: 5.5,
      // Retained generic metadata is not the source profile's trigger region:
      // source jumps use sample windows/full road width, and flat source pads
      // derive their footprint from the prepared layout rather than this box.
      halfWidth: definitionHalfWidth(definition) * CUSTOM_RAMP_HALF_WIDTH_RATIO,
      surface: "main",
      ...(classicJump ? { classic: classicJump } : {}),
    };
    if (ramp.classic) Object.freeze(ramp.classic);
    return Object.freeze(ramp);
  });
  Object.freeze(ramps);
  return { definition, samples, length: layout.length, ramps, shortcut: null };
}

function buildRuntime(definition: TrackDefinition): TrackRuntime {
  const points = definition.controlPoints;
  const point = (index: number) => points[((index % points.length) + points.length) % points.length];
  const curvePoint = (u: number): Vec3 => {
    const scaled = (((u % 1) + 1) % 1) * points.length; const segment = Math.floor(scaled); const t = scaled - segment;
    const p0 = point(segment - 1); const p1 = point(segment); const p2 = point(segment + 1); const p3 = point(segment + 2);
    return { x: catmullRom(t, p0.x, p1.x, p2.x, p3.x) * PLANAR_COURSE_SCALE, y: catmullRom(t, p0.y, p1.y, p2.y, p3.y), z: catmullRom(t, p0.z, p1.z, p2.z, p3.z) * PLANAR_COURSE_SCALE };
  };
  // Uniform Catmull-Rom is continuous, but tightly spaced authored points can
  // still concentrate most of a direction change into only a few metres. A
  // short circular low-pass over the dense centreline rounds those kinks at
  // road scale without flattening the larger S-bends or authored crossovers.
  // This also keeps the course radius compatible with the player's real
  // full-speed turning circle instead of relying on invisible steering help.
  // Round the authored spline over roughly a kart-length-scale window.  This
  // removes the small curvature spikes produced where Catmull-Rom segments
  // meet without sanding away the larger S-bends, crossroads and flyovers.
  // The wider window also keeps the road-edge ribbons from folding inward on
  // a corner that even the heavy kart cannot physically hold.
  const smoothClosed = (input: readonly Vec3[], radius = 10, passes = 3): Vec3[] => {
    let output = input.map((value) => ({ ...value }));
    for (let pass = 0; pass < passes; pass += 1) {
      output = output.map((_, index) => {
        let x = 0; let y = 0; let z = 0; let total = 0;
        for (let offset = -radius; offset <= radius; offset += 1) {
          const weight = radius + 1 - Math.abs(offset);
          const point = output[(index + offset + output.length) % output.length];
          x += point.x * weight; y += point.y * weight; z += point.z * weight; total += weight;
        }
        return { x: x / total, y: y / total, z: z / total };
      });
    }
    return output;
  };
  const planarLength = (input: readonly Vec3[]) => input.reduce((total, value, index) => {
    const next = input[(index + 1) % input.length];
    return total + Math.hypot(next.x - value.x, next.z - value.z);
  }, 0);
  // A 120cc Heavy kart needs roughly a 29 m centreline radius at full lock.
  // Keep a small margin above that physical limit so ordinary bends are
  // driveable without mandatory braking and the 10 m road half-width does not
  // collapse into needle-like inner borders.
  const fairTightCorners = (input: readonly Vec3[], minimumRadius = 48, passes = 26): Vec3[] => {
    const originalPlanarLength = planarLength(input);
    let output = input.map((value) => ({ ...value }));
    const halfWindow = 3;
    for (let pass = 0; pass < passes; pass += 1) {
      output = output.map((value, index) => {
        const before = output[(index - halfWindow + output.length) % output.length];
        const after = output[(index + halfWindow) % output.length];
        const ab = Math.hypot(value.x - before.x, value.z - before.z);
        const bc = Math.hypot(after.x - value.x, after.z - value.z);
        const ca = Math.hypot(before.x - after.x, before.z - after.z);
        const twiceArea = Math.abs((value.x - before.x) * (after.z - before.z) - (value.z - before.z) * (after.x - before.x));
        const radius = twiceArea < 1e-5 ? Number.POSITIVE_INFINITY : ab * bc * ca / (2 * twiceArea);
        if (radius >= minimumRadius) return { ...value };
        const targetX = (before.x + after.x) * 0.5;
        const targetZ = (before.z + after.z) * 0.5;
        const dx = targetX - value.x; const dz = targetZ - value.z;
        const distance = Math.hypot(dx, dz) || 1;
        const requested = Math.min(1.5, distance * Math.min(0.52, (minimumRadius - radius) / minimumRadius));
        return { x: value.x + dx / distance * requested, y: value.y, z: value.z + dz / distance * requested };
      });
    }
    // Local fairing shortens only the acute inside arcs. Restore the authored
    // footprint so fixing one needle corner never quietly shortens the race.
    const scale = originalPlanarLength / Math.max(1, planarLength(output));
    const centre = output.reduce((sum, value) => ({ x: sum.x + value.x / output.length, z: sum.z + value.z / output.length }), { x: 0, z: 0 });
    return output.map((value) => ({ ...value, x: centre.x + (value.x - centre.x) * scale, z: centre.z + (value.z - centre.z) * scale }));
  };
  const centreline = fairTightCorners(smoothClosed(Array.from({ length: SAMPLE_COUNT }, (_, index) => curvePoint(index / SAMPLE_COUNT))));
  const samples: TrackRuntime["samples"] = []; let length = 0; let previous = centreline[0];
  for (let i = 0; i <= SAMPLE_COUNT; i += 1) {
    const current = centreline[i % SAMPLE_COUNT];
    if (i > 0) length += Math.hypot(current.x - previous.x, current.y - previous.y, current.z - previous.z);
    samples.push({ ...current, distance: length }); previous = current;
  }
  const partial = { samples, length };
  const shortcutSpec = definition.shortcut;
  let shortcut: ShortcutDefinition | null = null;
  let shortcutRamps: RampDefinition[] = [];
  if (shortcutSpec) {
    const startDistance = length * shortcutSpec.startProgress; const endDistance = length * shortcutSpec.endProgress;
    const startFrame = sampleRuntime(partial, startDistance); const endFrame = sampleRuntime(partial, endDistance);
    const centreDx = endFrame.position.x - startFrame.position.x; const centreDz = endFrame.position.z - startFrame.position.z;
    const derivedLeaveSide = Math.sign(centreDx * startFrame.normal.x + centreDz * startFrame.normal.z) || 1;
    const derivedEnterSide = Math.sign(-centreDx * endFrame.normal.x - centreDz * endFrame.normal.z) || 1;
    const [leaveSide, enterSide] = shortcutSpec.endpointSides ?? [derivedLeaveSide, derivedEnterSide];
    // Keep separated branches authored at a meaningful edge distance while
    // retaining the historical compact portal for legacy course cuts.
    const branchOffset = shortcutSpec.branchOffset ?? (definitionHalfWidth(definition) - shortcutSpec.halfWidth * 0.62);
    // A shortcut is a side branch, not a full-width diagonal painted from the
    // centre of one road section to another. Anchor both ends near the relevant
    // road edge so the split reads cleanly and does not occupy half the course.
    const start = { x: startFrame.position.x + startFrame.normal.x * leaveSide * branchOffset, y: startFrame.position.y, z: startFrame.position.z + startFrame.normal.z * leaveSide * branchOffset };
    const end = { x: endFrame.position.x + endFrame.normal.x * enterSide * branchOffset, y: endFrame.position.y, z: endFrame.position.z + endFrame.normal.z * enterSide * branchOffset };
    const chordX = end.x - start.x; const chordZ = end.z - start.z; const chordLength = Math.hypot(chordX, chordZ) || 1;
    const chordForward = { x: chordX / chordLength, y: 0, z: chordZ / chordLength };
    const chordSide = { x: chordForward.z, y: 0, z: -chordForward.x };
    const distance3 = (a: Vec3, b: Vec3) => Math.hypot(a.x - b.x, a.y - b.y, a.z - b.z);
    const normalized = (value: Vec3): Vec3 => {
      const magnitude = Math.hypot(value.x, value.y, value.z) || 1;
      return { x: value.x / magnitude, y: value.y / magnitude, z: value.z / magnitude };
    };
    const startTangent = normalized(startFrame.tangent); const endTangent = normalized(endFrame.tangent);
    const [startHandle, endHandle] = shortcutSpec.endpointHandles;
    const controlA = { x: start.x + startTangent.x * startHandle, y: start.y + startTangent.y * startHandle, z: start.z + startTangent.z * startHandle };
    const controlB = { x: end.x - endTangent.x * endHandle, y: end.y - endTangent.y * endHandle, z: end.z - endTangent.z * endHandle };
    const cubicAt = (progress: number): Vec3 => {
      const inverse = 1 - progress; return {
        x: inverse ** 3 * start.x + 3 * inverse ** 2 * progress * controlA.x + 3 * inverse * progress ** 2 * controlB.x + progress ** 3 * end.x,
        y: inverse ** 3 * start.y + 3 * inverse ** 2 * progress * controlA.y + 3 * inverse * progress ** 2 * controlB.y + progress ** 3 * end.y,
        z: inverse ** 3 * start.z + 3 * inverse ** 2 * progress * controlA.z + 3 * inverse * progress ** 2 * controlB.z + progress ** 3 * end.z,
      };
    };
    const sortedAnchors = [...shortcutSpec.anchors].sort((a, b) => a.progress - b.progress);
    const authoredOffsets = sortedAnchors.map((anchor): Vec3 => ({
      x: chordForward.x * anchor.along + chordSide.x * anchor.lateral,
      y: anchor.lift,
      z: chordForward.z * anchor.along + chordSide.z * anchor.lateral,
    }));
    const offsetAt = (progress: number): Vec3 => {
      if (authoredOffsets.length < 2) return { x: 0, y: 0, z: 0 };
      const target = Math.max(0, Math.min(1, progress)); let segment = authoredOffsets.length - 2;
      for (let index = 0; index < sortedAnchors.length - 1; index += 1) {
        if (target <= sortedAnchors[index + 1].progress || index === sortedAnchors.length - 2) { segment = index; break; }
      }
      const segmentStart = sortedAnchors[segment].progress; const span = sortedAnchors[segment + 1].progress - segmentStart || 1;
      const local = Math.max(0, Math.min(1, (target - segmentStart) / span));
      const p0 = authoredOffsets[Math.max(0, segment - 1)]; const p1 = authoredOffsets[segment];
      const p2 = authoredOffsets[segment + 1]; const p3 = authoredOffsets[Math.min(authoredOffsets.length - 1, segment + 2)];
      // A bounded envelope keeps the authored S/gap shape inside the branch
      // while its derivative is zero at both junctions. The endpoint handles,
      // rather than a sharp offset jump, own entry and exit continuity.
      const envelope = Math.sin(Math.PI * target) ** 2 * shortcutSpec.anchorInfluence;
      return { x: catmullRom(local, p0.x, p1.x, p2.x, p3.x) * envelope, y: catmullRom(local, p0.y, p1.y, p2.y, p3.y) * envelope, z: catmullRom(local, p0.z, p1.z, p2.z, p3.z) * envelope };
    };
    const routeAt = (progress: number): Vec3 => {
      const base = cubicAt(progress); const offset = offsetAt(progress);
      return { x: base.x + offset.x, y: base.y + offset.y, z: base.z + offset.z };
    };
    interface ShortcutCurveSample { position: Vec3; tangent: Vec3; normal: Vec3; parameter: number; routeDistance: number; }
    const curveSamples: ShortcutCurveSample[] = []; let shortcutLength = 0; let shortcutPrevious = start;
    for (let index = 0; index <= SHORTCUT_SAMPLE_COUNT; index += 1) {
      const parameter = index / SHORTCUT_SAMPLE_COUNT; const position = routeAt(parameter);
      if (index > 0) shortcutLength += distance3(shortcutPrevious, position);
      const tangentRaw = index === 0 ? startTangent : index === SHORTCUT_SAMPLE_COUNT ? endTangent : (() => {
        const epsilon = 1 / SHORTCUT_SAMPLE_COUNT;
        const before = routeAt(parameter - epsilon); const after = routeAt(parameter + epsilon);
        // The finite difference is already the derivative of the complete
        // authored route (base cubic plus anchor offset); do not add the base
        // derivative a second time.
        return { x: (after.x - before.x) / (2 * epsilon), y: (after.y - before.y) / (2 * epsilon), z: (after.z - before.z) / (2 * epsilon) };
      })();
      const tangent = normalized(tangentRaw); const horizontalLength = Math.hypot(tangent.x, tangent.z) || 1;
      curveSamples.push({ position, tangent, normal: { x: tangent.z / horizontalLength, y: 0, z: -tangent.x / horizontalLength }, parameter, routeDistance: shortcutLength });
      shortcutPrevious = position;
    }
    const bypassedRoadLength = endDistance - startDistance;
    const generated = { samples: curveSamples, length: shortcutLength };
    const shortcutSamples: ShortcutSample[] = generated.samples.map((sample) => ({
      position: sample.position,
      tangent: sample.tangent,
      normal: sample.normal,
      distance: startDistance + bypassedRoadLength * (generated.length > 0 ? sample.routeDistance / generated.length : 0),
      routeDistance: sample.routeDistance,
      progress: generated.length > 0 ? sample.routeDistance / generated.length : 0,
    }));
    const routeToCourse = (progress: number) => startDistance + bypassedRoadLength * Math.max(0, Math.min(1, progress));
    shortcutRamps = shortcutSpec.jumps.map((jump, index) => ({
      id: `${definition.id}-shortcut-ramp-${index + 1}`,
      distance: routeToCourse(jump.rampProgress),
      // Ramp half-length is authored in route metres; convert to course-distance
      // metres because kart.distance advances at the bypass rate on a shortcut.
      halfLength: 3.2 * (bypassedRoadLength / Math.max(1, generated.length)),
      halfWidth: shortcutSpec.halfWidth,
      surface: "shortcut",
    }));
    const gaps: ShortcutGap[] = shortcutSpec.jumps.map((jump) => ({
      startDistance: routeToCourse(jump.gapStartProgress),
      endDistance: routeToCourse(jump.gapEndProgress),
      startRouteDistance: generated.length * jump.gapStartProgress,
      endRouteDistance: generated.length * jump.gapEndProgress,
    }));
    shortcut = { ...shortcutSpec, startDistance, endDistance, start, end, normal: shortcutSamples[0].normal, length: generated.length, samples: shortcutSamples, ramps: shortcutRamps, gaps };
  }
  const ramps: RampDefinition[] = [
    ...definition.rampProgress.map((progress, index): RampDefinition => ({ id: `${definition.id}-ramp-${index + 1}`, distance: length * progress, halfLength: 5.5, halfWidth: 6.5, surface: "main" })),
    ...shortcutRamps,
  ];
  return { definition, samples, length, ramps, shortcut };
}

/** Register one detached, validated custom course definition. */
export function registerCustomTrack(id: string, input: unknown): TrackDefinition {
  const registryId = normalizeCustomRegistryId(id);
  // Build and derive everything before replacing an existing entry. If
  // validation/layout preparation fails, the old custom course remains live.
  const layout = freezeCustomTrackLayout(buildCustomTrackLayout(input));
  const definition = freezeTrackDefinition(customTrackDefinition(registryId, layout));
  customLayoutById.set(registryId, layout);
  customDefinitionById.set(registryId, definition);
  runtimeById.delete(registryId);
  return definition;
}

/** Remove a registered custom course and its lazily built runtime, if present. */
export function unregisterCustomTrack(id: string): boolean {
  const registryId = normalizeCustomRegistryId(id);
  const existed = customDefinitionById.delete(registryId) || customLayoutById.delete(registryId) || runtimeById.has(registryId);
  runtimeById.delete(registryId);
  customLayoutById.delete(registryId);
  return existed;
}

/** Prepared legacy-style layout for a saved custom course or static classic.
 * Authored v2 tour courses return null; classics are prepared lazily. */
export function getCustomTrackLayout(id: string): CustomTrackLayout | null {
  if (id.startsWith("classic-")) {
    ensureClassicTrack(id);
    return customLayoutById.get(id)!;
  }
  if (!isCustomTrackId(id)) {
    if (id.startsWith("c_") || id === CUSTOM_TRACK_PREVIEW_KEY) normalizeCustomRegistryId(id);
    return null;
  }
  const layout = customLayoutById.get(id);
  if (!layout) throw new Error(`Unknown custom track "${id}"; register it before use`);
  return layout;
}

/**
 * Whether this route needs the source-local centreline cursor.  Classic
 * originals always keep that cursor for their checkpoint rules; a custom
 * route gets it only when it explicitly carries legacy jump markers.  The
 * latter deliberately does not imply classic checkpoints or lap accounting.
 */
export function usesSourceIndexedCursor(id: string): boolean {
  if (isClassicTrackId(id)) return true;
  const layout = getCustomTrackLayout(id);
  return Boolean(layout?.definition.jumps?.length);
}

function getRuntime(trackId = DEFAULT_TRACK_ID): TrackRuntime {
  ensureClassicTrack(trackId);
  const customDefinition = customDefinitionById.get(trackId);
  if (customDefinition) {
    let runtime = runtimeById.get(trackId);
    if (!runtime) {
      const layout = customLayoutById.get(trackId);
      if (!layout) throw new Error(`Custom track "${trackId}" is missing its registered layout`);
      runtime = buildRuntimeFromCustomLayout(customDefinition, layout);
      runtimeById.set(trackId, runtime);
    }
    return runtime;
  }
  if (trackId.startsWith("c_") || trackId === CUSTOM_TRACK_PREVIEW_KEY) {
    throw new Error(`Unknown custom track "${trackId}"; register it before use`);
  }
  const definition = definitionById.get(trackId) ?? definitionById.get(DEFAULT_TRACK_ID)!;
  let runtime = runtimeById.get(definition.id);
  if (!runtime) { runtime = buildRuntime(definition); runtimeById.set(definition.id, runtime); }
  return runtime;
}

export function getTrackDefinition(trackId = DEFAULT_TRACK_ID): TrackDefinition {
  const runtime = getRuntime(trackId);
  return runtime.definition;
}
export function getTrackLength(trackId = DEFAULT_TRACK_ID): number { return getRuntime(trackId).length; }

/** Find the prepared source sample nearest a route distance for cursor seeding
 * and recovery.  Source-indexed callers use the closed lap's non-duplicate
 * sample count, matching the legacy `Math.round(u * N) % N` modulus. */
export function sourceSampleIndexAtDistance(trackId = DEFAULT_TRACK_ID, distance = 0): number | undefined {
  if (!usesSourceIndexedCursor(trackId)) return undefined;
  const runtime = getRuntime(trackId);
  const sampleCount = runtime.samples.length - 1;
  if (sampleCount <= 0 || !Number.isFinite(runtime.length) || runtime.length <= 0) return 0;
  const numericDistance = Number.isFinite(distance) ? distance : 0;
  const wrapped = ((numericDistance % runtime.length) + runtime.length) % runtime.length;
  let low = 0;
  let high = sampleCount - 1;
  while (low < high) {
    const middle = (low + high) >> 1;
    if (runtime.samples[middle].distance < wrapped) low = middle + 1;
    else high = middle;
  }
  let best = low;
  let bestDelta = Number.POSITIVE_INFINITY;
  for (const candidate of [low - 1, low, low + 1, 0, sampleCount - 1]) {
    const index = ((candidate % sampleCount) + sampleCount) % sampleCount;
    const rawDelta = Math.abs(runtime.samples[index].distance - wrapped);
    const delta = Math.min(rawDelta, runtime.length - rawDelta);
    if (delta < bestDelta) {
      best = index;
      bestDelta = delta;
    }
  }
  return best;
}
export function getRampDefinitions(trackId = DEFAULT_TRACK_ID): readonly RampDefinition[] { return getRuntime(trackId).ramps; }
export function getTrackHalfWidth(trackId = DEFAULT_TRACK_ID): number { return definitionHalfWidth(getRuntime(trackId).definition); }
export function getSignatureZoneDefinition(trackId = DEFAULT_TRACK_ID): SignatureZoneDefinition | null {
  const runtime = getRuntime(trackId); const zone = runtime.definition.signatureZone;
  return zone ? { ...zone, distance: runtime.length * zone.progress } : null;
}
export const trackControlPoints = getTrackDefinition().controlPoints;
export const trackLength = getTrackLength();
export const rampDefinitions = getRampDefinitions();
export function sampleTrack(distance: number, trackId = DEFAULT_TRACK_ID): TrackFrame { return sampleRuntime(getRuntime(trackId), distance); }
export function worldPosition(distance: number, lateral: number, lift = 0, trackId = DEFAULT_TRACK_ID): Vec3 {
  const frame = sampleTrack(distance, trackId);
  return { x: frame.position.x + frame.normal.x * lateral, y: frame.position.y + lift, z: frame.position.z + frame.normal.z * lateral };
}

export function projectToTrack(x: number, z: number, trackId = DEFAULT_TRACK_ID, referenceY?: number, previousDistance?: number, previousSampleIndex?: number): TrackProjection {
  const runtime = getRuntime(trackId); let bestIndex = 0; let bestT = 0; let bestDistanceSq = Number.POSITIVE_INFINITY; let bestScore = Number.POSITIVE_INFINITY;
  const sampleCount = runtime.samples.length - 1;
  let localSampleIndex: number | undefined;
  if (usesSourceIndexedCursor(trackId) && previousSampleIndex !== undefined && Number.isFinite(previousSampleIndex)) {
    // v1 localCI searches only +/-24 source samples. Once the road leg is
    // known, a nearby crossover must not become a new progress reference.
    let nearest = Number.POSITIVE_INFINITY;
    for (let offset = -24; offset <= 24; offset++) {
      const index = ((Math.floor(previousSampleIndex) + offset) % sampleCount + sampleCount) % sampleCount;
      const point = runtime.samples[index];
      const squared = (x - point.x) ** 2 + (z - point.z) ** 2;
      if (squared < nearest) { nearest = squared; localSampleIndex = index; }
    }
  }
  const segmentCount = localSampleIndex === undefined ? sampleCount : 2;
  for (let candidate = 0; candidate < segmentCount; candidate += 1) {
    const i = localSampleIndex === undefined ? candidate : (localSampleIndex - 1 + candidate + sampleCount) % sampleCount;
    const a = runtime.samples[i]; const b = runtime.samples[i + 1]; const dx = b.x - a.x; const dy = b.y - a.y; const dz = b.z - a.z;
    const denominator = dx * dx + dz * dz || 1; const t = Math.max(0, Math.min(1, ((x - a.x) * dx + (z - a.z) * dz) / denominator));
    const px = a.x + dx * t; const py = a.y + dy * t; const pz = a.z + dz * t; const distanceSq = (x - px) ** 2 + (z - pz) ** 2;
    // Crossovers have two valid road decks at the same X/Z. When a caller has
    // a physical height, prefer the matching deck; callers without Y keep the
    // old top-down behaviour used by the minimap and placement tools.
    const verticalSq = referenceY === undefined ? 0 : (referenceY - py) ** 2;
    const candidateDistance = a.distance + (b.distance - a.distance) * t;
    const wrappedPrevious = previousDistance === undefined ? undefined : ((previousDistance % runtime.length) + runtime.length) % runtime.length;
    const routeGap = wrappedPrevious === undefined ? 0 : Math.min(Math.abs(candidateDistance - wrappedPrevious), runtime.length - Math.abs(candidateDistance - wrappedPrevious));
    const score = distanceSq + verticalSq * 2.25 + Math.min(64, routeGap * routeGap * 0.006);
    if (score < bestScore) { bestScore = score; bestDistanceSq = distanceSq; bestIndex = i; bestT = t; }
  }
  const a = runtime.samples[bestIndex]; const b = runtime.samples[bestIndex + 1]; const distance = a.distance + (b.distance - a.distance) * bestT;
  const frame = frameFromSegment(runtime, bestIndex, bestT, distance);
  const lateral = (x - frame.position.x) * frame.normal.x + (z - frame.position.z) * frame.normal.z;
  const sampleIndex = localSampleIndex ?? (sampleCount > 0 ? (bestIndex + (bestT >= 0.5 ? 1 : 0)) % sampleCount : 0);
  const mainProjection: TrackProjection = { ...frame, lateral, distanceSq: bestDistanceSq, halfWidth: definitionHalfWidth(runtime.definition) - 1.15, surface: "main", sampleIndex };
  const shortcut = runtime.shortcut;
  if (shortcut) {
    let shortcutBest: TrackProjection | null = null; let shortcutBestScore = Number.POSITIVE_INFINITY;
    for (let index = 0; index < shortcut.samples.length - 1; index += 1) {
      const shortcutA = shortcut.samples[index]; const shortcutB = shortcut.samples[index + 1];
      const sx = shortcutB.position.x - shortcutA.position.x; const sz = shortcutB.position.z - shortcutA.position.z;
      const denominator = sx * sx + sz * sz || 1; const t = Math.max(0, Math.min(1, ((x - shortcutA.position.x) * sx + (z - shortcutA.position.z) * sz) / denominator));
      const position = { x: shortcutA.position.x + sx * t, y: shortcutA.position.y + (shortcutB.position.y - shortcutA.position.y) * t, z: shortcutA.position.z + sz * t };
      const distanceSq = (x - position.x) ** 2 + (z - position.z) ** 2;
      const candidateDistance = shortcutA.distance + (shortcutB.distance - shortcutA.distance) * t;
      const wrappedPrevious = previousDistance === undefined ? undefined : ((previousDistance % runtime.length) + runtime.length) % runtime.length;
      const routeGap = wrappedPrevious === undefined ? 0 : Math.min(Math.abs(candidateDistance - wrappedPrevious), runtime.length - Math.abs(candidateDistance - wrappedPrevious));
      const score = distanceSq + (referenceY === undefined ? 0 : (referenceY - position.y) ** 2 * 2.25) + Math.min(64, routeGap * routeGap * 0.006);
      if (score >= shortcutBestScore) continue;
      const tangentRaw = { x: shortcutA.tangent.x + (shortcutB.tangent.x - shortcutA.tangent.x) * t, y: shortcutA.tangent.y + (shortcutB.tangent.y - shortcutA.tangent.y) * t, z: shortcutA.tangent.z + (shortcutB.tangent.z - shortcutA.tangent.z) * t };
      const tangentLength = Math.hypot(tangentRaw.x, tangentRaw.y, tangentRaw.z) || 1; const tangent = { x: tangentRaw.x / tangentLength, y: tangentRaw.y / tangentLength, z: tangentRaw.z / tangentLength };
      const horizontalLength = Math.hypot(tangent.x, tangent.z) || 1; const normal = { x: tangent.z / horizontalLength, y: 0, z: -tangent.x / horizontalLength };
      const lateral = (x - position.x) * normal.x + (z - position.z) * normal.z;
      shortcutBest = { position, tangent, normal, distance: shortcutA.distance + (shortcutB.distance - shortcutA.distance) * t, lateral, distanceSq, halfWidth: shortcut.halfWidth - 0.65, surface: "shortcut" };
      shortcutBestScore = score;
    }
    if (shortcutBest && shortcutBest.distance > shortcut.startDistance + 0.001 && shortcutBest.distance < shortcut.endDistance - 0.001 && Math.abs(shortcutBest.lateral) <= shortcut.halfWidth && shortcutBestScore < bestScore) return shortcutBest;
  }
  return mainProjection;
}

export function getShortcutDefinition(trackId = DEFAULT_TRACK_ID): ShortcutDefinition | null { return getRuntime(trackId).shortcut; }
/** The floorless shortcut span containing this course distance, if any. */
export function getShortcutGapAt(distance: number, trackId = DEFAULT_TRACK_ID): ShortcutGap | null {
  const runtime = getRuntime(trackId); const shortcut = runtime.shortcut;
  if (!shortcut) return null;
  const wrapped = ((distance % runtime.length) + runtime.length) % runtime.length;
  for (const gap of shortcut.gaps) if (wrapped >= gap.startDistance && wrapped <= gap.endDistance) return gap;
  return null;
}
export function sampleShortcut(progress: number, trackId = DEFAULT_TRACK_ID): ShortcutSample {
  const shortcut = getRuntime(trackId).shortcut;
  if (!shortcut) throw new Error(`Track "${trackId}" does not define a shortcut route`);
  const samples = shortcut.samples; const target = Math.max(0, Math.min(1, progress)); let index = 0;
  while (index < samples.length - 2 && samples[index + 1].progress < target) index += 1;
  const a = samples[index]; const b = samples[index + 1]; const span = b.progress - a.progress || 1; const t = Math.max(0, Math.min(1, (target - a.progress) / span));
  const tangentRaw = { x: a.tangent.x + (b.tangent.x - a.tangent.x) * t, y: a.tangent.y + (b.tangent.y - a.tangent.y) * t, z: a.tangent.z + (b.tangent.z - a.tangent.z) * t };
  const tangentLength = Math.hypot(tangentRaw.x, tangentRaw.y, tangentRaw.z) || 1; const tangent = { x: tangentRaw.x / tangentLength, y: tangentRaw.y / tangentLength, z: tangentRaw.z / tangentLength };
  const horizontalLength = Math.hypot(tangent.x, tangent.z) || 1;
  return { position: { x: a.position.x + (b.position.x - a.position.x) * t, y: a.position.y + (b.position.y - a.position.y) * t, z: a.position.z + (b.position.z - a.position.z) * t }, tangent, normal: { x: tangent.z / horizontalLength, y: 0, z: -tangent.x / horizontalLength }, distance: a.distance + (b.distance - a.distance) * t, routeDistance: a.routeDistance + (b.routeDistance - a.routeDistance) * t, progress: target };
}
export function distanceToShortcut(x: number, z: number, trackId = DEFAULT_TRACK_ID): number {
  const shortcut = getRuntime(trackId).shortcut;
  if (!shortcut) return Number.POSITIVE_INFINITY;
  const samples = shortcut.samples; let bestDistanceSq = Number.POSITIVE_INFINITY;
  for (let index = 0; index < samples.length - 1; index += 1) {
    const a = samples[index].position; const b = samples[index + 1].position; const dx = b.x - a.x; const dz = b.z - a.z;
    const denominator = dx * dx + dz * dz || 1; const t = Math.max(0, Math.min(1, ((x - a.x) * dx + (z - a.z) * dz) / denominator));
    bestDistanceSq = Math.min(bestDistanceSq, (x - (a.x + dx * t)) ** 2 + (z - (a.z + dz * t)) ** 2);
  }
  return Math.sqrt(bestDistanceSq);
}
export function wrappedDistanceDelta(distance: number, target: number, trackId = DEFAULT_TRACK_ID): number {
  const length = getTrackLength(trackId); const wrapped = ((distance % length) + length) % length; let delta = target - wrapped;
  if (delta > length * 0.5) delta -= length; if (delta < -length * 0.5) delta += length; return delta;
}
